import React from 'react'
import ReactMarkdown, { Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { COLORS, styled } from '../../theme'
import { Link } from '../Link'
import { Typography } from '../Typography'
import { sanitizeuUrl } from './url-sanitizer'

const ListTypography = styled(Typography)`
  display: flex;
  flex-direction: column;
`

const Pre = styled(Typography)`
  background-color: ${COLORS.divider};
  padding: 2px 4px;
  border-radius: 2px;
  overflow: auto;
`

const InlineCode = styled(Typography)`
  background-color: ${COLORS.divider};
  padding: 2px 4px;
  border-radius: 2px;
`

const StyledLink = styled(Link)`
  color: ${COLORS.primary.main};
`

const HeaderTypography = styled(Typography)`
  margin: 16px 0 8px;
`

const ParagraphTypography = styled(Typography)`
  &:first-of-type {
    margin: 0;
  }

  margin: 8px 0 0;
`

interface Props {
  children: string
  componentsOverrides?: Components
}

export function Markdown(props: Props) {
  /**
   * Normalize the input to ensure consistent line breaks.
   * This is a modern standard for rendering Markdown correctly.
   */
  const markdownChildren = props.children.replace(/\r\n/g, '\n')

  /**
   * Use Typography components to render Markdown elements.
   * This ensures consistent styling with the rest of the application.
   */
  const defaultComponents: Components = {
    p: ({ children }) => (
      <ParagraphTypography variant="body2" component="p">
        {children}
      </ParagraphTypography>
    ),
    h1: ({ children }) => <HeaderTypography variant="h1">{children}</HeaderTypography>,
    h2: ({ children }) => <HeaderTypography variant="h2">{children}</HeaderTypography>,
    h3: ({ children }) => <HeaderTypography variant="h3">{children}</HeaderTypography>,
    h4: ({ children }) => <HeaderTypography variant="h4">{children}</HeaderTypography>,
    h5: ({ children }) => <HeaderTypography variant="h5">{children}</HeaderTypography>,
    h6: ({ children }) => <HeaderTypography variant="h6">{children}</HeaderTypography>,
    ul: ({ children }) => (
      <ListTypography variant="body2" component="ul">
        {children}
      </ListTypography>
    ),
    ol: ({ children }) => (
      <ListTypography variant="body2" component="ol">
        {children}
      </ListTypography>
    ),
    li: ({ children }) => {
      /**
       * Filter out unwanted line breaks that can occur due to how ReactMarkdown processes list items. This ensures cleaner rendering of list items without unnecessary whitespace.
       */
      const cleaned = React.Children.toArray(children).filter(
        (child, index) => !(index === 0 && typeof child === 'string' && child === '\n'),
      )
      return (
        <Typography variant="body2" component="li">
          {cleaned}
        </Typography>
      )
    },
    code: ({ inline, children }) =>
      inline ? (
        <InlineCode variant="caption" component="code">
          {children}
        </InlineCode>
      ) : (
        <Pre variant="body2" component="pre">
          <Typography variant="caption" component="code">
            {children}
          </Typography>
        </Pre>
      ),
    pre: ({ children }) => children,
    a: ({ children, ...attr }) => (
      <StyledLink underline="none" href={`${sanitizeuUrl(attr.href as string)}`}>
        {children}
      </StyledLink>
    ),
  }

  /**
   * Merge default components with any overrides provided.
   */
  const components: Components = {
    ...defaultComponents,
    ...props.componentsOverrides,
  }

  return (
    <ReactMarkdown components={components} remarkPlugins={[remarkGfm]}>
      {markdownChildren}
    </ReactMarkdown>
  )
}
